/* eslint-disable @next/next/no-img-element */

import * as React from 'react';

import clsxm from '@/lib/clsxm';
import { useHeroContext } from '@/hooks/use-hero-context';
import useInterpolation from '@/hooks/use-interpolation';

import HygraphRichText, {
  HygraphRichTextProps,
} from '@/components/hygraph-rich-text';
import Link, { LinkProps, LinkVariant } from '@/components/link';
import { Typography, TypographyColor } from '@/components/typography';

import { HorizontalAlignment } from '@/enums/horizontal-alignment';

export type ContentSectionProps = {
  align?: HorizontalAlignment;
  caption?: string;
  title?: string;
  titleAs?: string;
  body?: HygraphRichTextProps;
  useCheckmarkForLIs?: boolean;
  link?: LinkProps;
  color?: TypographyColor;
  buttonAlign?: HorizontalAlignment;
  headerClassName?: string;
  containerClassName?: string;
  overline?: boolean;
};

export default function ContentSection({
  overline = true,
  containerClassName,
  ...props
}: ContentSectionProps) {
  const interpolate = useInterpolation();

  const containerClasses = [];
  let buttonContainerClasses = '';

  const { isHero } = useHeroContext();

  if (props.align === HorizontalAlignment.CENTER) {
    containerClasses.push('text-center');
  } else if (props.align === HorizontalAlignment.RIGHT) {
    containerClasses.push('text-right');
  }

  if (props.buttonAlign === HorizontalAlignment.CENTER) {
    buttonContainerClasses = 'text-center';
  }

  return (
    <div className={clsxm('my-5', containerClassName, containerClasses)}>
      {(props.caption || props.title) && (
        <div className='flex flex-col'>
          {props.caption && !isHero && (
            <Typography.Subtitle2 color={props.color} overline={overline}>
              {interpolate(props.caption)}
            </Typography.Subtitle2>
          )}
          {props.caption && isHero && (
            <Typography.Subtitle2
              color={props.color}
              className='mb-3 font-[20px]'
              as='h1'
            >
              {interpolate(props.caption)}
            </Typography.Subtitle2>
          )}
          {props.title && !isHero && (
            <Typography.Heading5
              color={props.color}
              className={clsxm(props.headerClassName)}
              overline={!props.caption}
            >
              {interpolate(props.title)}
            </Typography.Heading5>
          )}
          {props.title && isHero && (
            <Typography.Heading1
              color={props.color}
              className={clsxm(props.headerClassName)}
              as='h2'
            >
              {interpolate(props.title)}
            </Typography.Heading1>
          )}
        </div>
      )}
      {props.body && (
        <div className='mt-3'>
          <HygraphRichText
            {...props.body}
            color={props.color}
            useCheckmarkForLIs={props.useCheckmarkForLIs}
          />
        </div>
      )}
      {props.link && (
        <div className={clsxm('mt-6', buttonContainerClasses)}>
          <Link variant={LinkVariant.PRIMARY_BUTTON} {...props.link} />
        </div>
      )}
    </div>
  );
}
